
/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

/**
 * @class CSVExport
 * @hideconstructor
 * @classdesc CSV Export Feature
 */

define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/CsvExportAPI'
], (APIFactory, CsvExportAPI) => {

	class CsvExporter {

		constructor(dashboard) {
			this.dashboard = dashboard;
			this.api = APIFactory.createAPI(this, [CsvExportAPI]);
		}

		destroy() {
			this.dashboard = null;
		}

		getAPI() {
			return this.api;
		}

		_toCSV(data) {
			if (Array.isArray (data) ) {
				const parseCellValue = (cellData) => {
					let value = cellData;
					if (isNaN(value)) {
						value = value.toString().trim();
						if (value.includes(',')) {
							value = `"${value.replace(/"/g, '""' )}"`;
						}
					}
					return value;
				};
				return data.map(row => row.map ? row.map(parseCellValue).join(',') : parseCellValue(row)).join('\n') + '\n';
			} else {
				throw new Error('The export data must be an array');
			}
		}

		/**
		 * @function CsvExportAPII#export
		 * @description Export a CSV file for supplied name & data
		 * @param {String} fileName The name of the CSV file
		 * @param {object[][]} data An array of data arrays to aggregate and export
		 * @return {object} returns a Blob containing the exported CSV data
		 */
		export(filename, ...data) {
			if (typeof filename !== 'string') {
				throw new Error('The filename must be a string');
			}
			const _window = this._getWindow();
			const csvDataArrays = data.map(this._toCSV.bind(this));
			const fullFileName = `${filename}.csv`;
			var csvBlob = new Blob(csvDataArrays, { type: 'text/csv' });
			if ( !filename ) throw new Error('Invalid filename');

			const anchor = this._createAnchor(_window);
			if (typeof anchor.download !== 'undefined') {
				const blobUrl = _window.URL.createObjectURL(csvBlob);
				anchor.setAttribute('href', blobUrl);
				anchor.setAttribute('download', fullFileName);
				anchor.style.display = 'none';
				_window.document.body.appendChild(anchor);
				anchor.click();
				_window.document.body.removeChild(anchor);
				setTimeout(_window.URL.revokeObjectURL.bind(this, blobUrl), 5000);
				return csvBlob;
			} else if (_window.navigator && _window.navigator.msSaveBlob) {
				//IE11 support
				_window.navigator.msSaveBlob(csvBlob, fullFileName);
				return csvBlob;
			} else {
				throw new Error('This browser does not support the ability to export');
			}
		}

		_getWindow() {
			return window;
		}

		_createAnchor(_window) {
			return _window.document.createElement('a');
		}

	}

	return CsvExporter;
});
